// Live product-card / product-row examples for the style-editing surfaces.
//
// It renders the REAL <ProductCard> — same markup, same `.storefront-product-card*` classes, same
// slot→inline-style plumbing the page previews use — rather than the abstract gray swatch that used
// to stand in for it. The point is that "what you see" and "what ships" are literally the same
// component: a title style that clips at two lines here clips at two lines on the storefront, and
// the 50-character name truncation shows up while you're still choosing a font.
//
// The card CSS is global (Editor/StorefrontEditor.css, imported by the editor screens), not scoped
// to the preview canvas, so the markup renders correctly inside a Fluent Panel or a callout too.
//
// The Search page's Rows view styles the same four dictionaries against a completely different
// shape, so ProductRowExample (below) mirrors the search preview's row instead: same
// `.sf-search-result*` classes, same slots, same left-image / right-body anatomy. The card and the
// row share the frame and caption so the panel reads the same either way.
//
// Self-contained by design: the sample product is hardcoded and its image is an inline SVG data URI
// authored below. No fetch, no tenant data — the example must paint instantly and identically for
// every storefront, and must not leak one storefront's catalog into another's styling surface.

import React from 'react';
import { Icon } from '@fluentui/react';
import { IStorefrontProductView } from '../../../models/StorefrontProducts';
import { IProductCardStyles } from './styleControls';
import { ProductCard } from './Sections/ProductCard';
// The row example borrows the search preview's row stylesheet. It is global CSS and already loaded
// wherever the search editor mounts; importing it here as well is idempotent and means the row
// still paints correctly when the panel is opened from somewhere that never mounted that editor.
import './StorefrontSearchPageEditor.css';

// A neutral laptop illustration: rounded lid, one accent-tinted display, a base bar and a soft
// ground shadow. Kept to flat shapes and a handful of colors so it stays legible at ~200px wide and
// never fights the styling being previewed. encodeURIComponent (not base64) so the source stays
// readable and editable in place.
const SAMPLE_IMAGE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 180">
  <ellipse cx="120" cy="152" rx="88" ry="7" fill="#000000" opacity="0.06"/>
  <rect x="46" y="22" width="148" height="104" rx="9" fill="#faf9f8" stroke="#c8c6c4" stroke-width="3"/>
  <rect x="57" y="33" width="126" height="82" rx="4" fill="#eef3f9"/>
  <rect x="57" y="33" width="126" height="15" rx="4" fill="#0f6cbd" opacity="0.9"/>
  <circle cx="66" cy="40" r="2.5" fill="#ffffff" opacity="0.85"/>
  <circle cx="74" cy="40" r="2.5" fill="#ffffff" opacity="0.6"/>
  <rect x="67" y="60" width="72" height="7" rx="3.5" fill="#c8c6c4"/>
  <rect x="67" y="74" width="106" height="6" rx="3" fill="#d9d7d5"/>
  <rect x="67" y="86" width="88" height="6" rx="3" fill="#d9d7d5"/>
  <rect x="67" y="98" width="34" height="8" rx="4" fill="#0f6cbd" opacity="0.35"/>
  <rect x="28" y="126" width="184" height="13" rx="6.5" fill="#d2d0ce"/>
  <rect x="104" y="130" width="32" height="5" rx="2.5" fill="#b3b0ad"/>
</svg>`;

const SAMPLE_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(SAMPLE_IMAGE_SVG)}`;

// Only the fields ProductCard reads carry meaning (name / image / basePrice); the rest satisfy the
// required members of IStorefrontProductView. The name is deliberately past 50 characters so the
// card's truncation is visible in the example instead of being a surprise on the live page.
const SAMPLE_PRODUCT: IStorefrontProductView = {
    id: 'example-product',
    name: 'Aurora X14 Business Ultrabook — 16 GB RAM, 512 GB SSD, Wi-Fi 7',
    image: SAMPLE_IMAGE,
    basePrice: 1299,
    inStock: true,
    priceLevels: {},
    costLevels: {},
};

// The card's stylesheet width is fixed at 250px, which is wider than the style panel's column.
// Scaling the whole card down is the only faithful option: overriding the width would change the
// layout being previewed (the name clamp, the image box, the cart button's corner), so instead the
// real 250px card is rendered and transform-scaled into the frame. The wrapper is sized to the
// SCALED box so the transform leaves no phantom whitespace beneath it.
const CARD_WIDTH = 250;
const CARD_HEIGHT = 300;
const PREVIEW_WIDTH = 200;
const SCALE = PREVIEW_WIDTH / CARD_WIDTH;

export interface IProductCardExampleProps {
    /** Resolved inline styles per slot — same shape ProductCard consumes. */
    cardStyles?: IProductCardStyles;
    /** Merged over the card slot while the pointer is over the example (demos the hover part). */
    hoverStyle?: React.CSSProperties;
    isDark?: boolean;
    caption?: string;
}

// The frame + caption both examples sit in, so a card panel and a row panel read as one family.
// It owns the pointer handlers because the hover demo is "point anywhere at the example": the tile
// itself is transform-scaled inside, and the merchant is aiming at the box, not at the transform.
const ExampleFrame: React.FC<{
    /** Size of the SCALED box, so the transform leaves no phantom whitespace under the frame. */
    width: number;
    height: number;
    caption: string;
    /** 'card' | 'row', which only reaches the hover hint in the caption. */
    noun: string;
    hasHover: boolean;
    onHoverChange: (hovered: boolean) => void;
    /** Dark frame for tiles that paint themselves as translucent white (see ProductRowExample). */
    darkBackdrop?: boolean;
    children?: React.ReactNode;
}> = ({ width, height, caption, noun, hasHover, onHoverChange, darkBackdrop, children }) => (
    <div>
        {/* Same frame as the swatch it replaces, so the two read as one family in the panel. */}
        <div style={{
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            border: `1px solid ${darkBackdrop ? '#3b3a39' : '#edebe9'}`,
            background: darkBackdrop ? '#26262b' : '#faf9f8',
            borderRadius: 4,
            padding: 12,
        }}>
            <div
                style={{ width, height }}
                onMouseEnter={() => onHoverChange(true)}
                onMouseLeave={() => onHoverChange(false)}
            >
                {children}
            </div>
        </div>
        <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
            {hasHover ? `${caption} · point at the ${noun} to preview hover styling` : caption}
        </div>
    </div>
);

// The hover part is a `:hover` rule on the live storefront (HOVER_PARTS in stylesConfig); here it's
// simulated by merging it over the card slot while the pointer is inside the frame, so the merchant
// can actually see what they're editing without a real page to hover. Shared by both examples.
const useHoverStyles = (cardStyles?: IProductCardStyles, hoverStyle?: React.CSSProperties) => {
    const [hovered, setHovered] = React.useState(false);
    const hasHover = !!hoverStyle && Object.keys(hoverStyle).length > 0;
    const effectiveStyles: IProductCardStyles | undefined = (hasHover && hovered)
        ? { ...cardStyles, card: { ...cardStyles?.card, ...hoverStyle } }
        : cardStyles;
    return { hasHover, effectiveStyles, setHovered };
};

const ProductCardExample: React.FC<IProductCardExampleProps> = ({ cardStyles, hoverStyle, isDark, caption }) => {
    const { hasHover, effectiveStyles, setHovered } = useHoverStyles(cardStyles, hoverStyle);

    return (
        <ExampleFrame
            width={PREVIEW_WIDTH}
            height={Math.round(CARD_HEIGHT * SCALE)}
            caption={caption ?? 'Example product — updates as you edit'}
            noun='card'
            hasHover={hasHover}
            onHoverChange={setHovered}
        >
            <div style={{ width: CARD_WIDTH, transform: `scale(${SCALE})`, transformOrigin: 'top left' }}>
                {/* No onEditStyle: this is an example, not an editable preview — the pencil
                    would offer to open a panel from inside the panel. The card's own
                    add-to-cart IconButton is inert (a bare button with no handler). */}
                <ProductCard product={SAMPLE_PRODUCT} isDark={isDark} cardStyles={effectiveStyles} />
            </div>
        </ExampleFrame>
    );
};

// ---- Row flavor (Search page, Rows view) ----

// The search row is fluid, but its internals are not: a 100px image, a 150px reserve for the
// actions column, and a name box clamped to two 24px lines. Squeezed into the ~265px the panel
// affords, those fixed parts would leave the name a sliver and clip it mid-word, so the row is
// laid out at a natural desktop-ish width and transform-scaled into the frame, the same bargain
// the card example strikes, just at a smaller ratio because the row is wider than it is tall.
const ROW_WIDTH = 500;
const ROW_HEIGHT = 160; // .sf-search-result is a fixed-height strip.
const ROW_PREVIEW_WIDTH = 260;
const ROW_SCALE = ROW_PREVIEW_WIDTH / ROW_WIDTH;

// Shorter than SAMPLE_PRODUCT.name on purpose: the card truncates at 50 characters (which is worth
// showing), while the row clips by HEIGHT with no ellipsis. At preview scale the full name would be
// cut off mid-word and read as a rendering bug rather than as the styling being edited.
const ROW_SAMPLE_NAME = 'Aurora X14 Ultrabook';

// Matches the search preview's formatter (and the storefront's row price).
const formatCurrency = (value: number) => value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/**
 * The row-shaped twin of ProductCardExample, for the Search page's Rows view. Same props, same
 * slot mapping (card → the strip, title → the name, price → the price, image → the image box),
 * so ProductCardStylePanel can swap one for the other on `modeNoun` alone.
 */
export const ProductRowExample: React.FC<IProductCardExampleProps> = ({ cardStyles, hoverStyle, isDark, caption }) => {
    const { hasHover, effectiveStyles, setHovered } = useHoverStyles(cardStyles, hoverStyle);

    // Mirrors the search preview: an image style only takes effect with overflow clipped, otherwise
    // a radius on the box does nothing to the picture inside it.
    const imageStyle = effectiveStyles?.image && Object.keys(effectiveStyles.image).length
        ? { ...effectiveStyles.image, overflow: 'hidden' as const }
        : undefined;

    return (
        <ExampleFrame
            width={ROW_PREVIEW_WIDTH}
            height={Math.round(ROW_HEIGHT * ROW_SCALE)}
            caption={caption ?? 'Example product — updates as you edit'}
            noun='row'
            hasHover={hasHover}
            onHoverChange={setHovered}
            // The dark row is translucent white (.sf-search-dark .sf-search-result), which would be
            // invisible on the default near-white frame, so the frame goes dark with it.
            darkBackdrop={isDark}
        >
            {/* Dark styling is class-based in the search stylesheet, so the wrapper carries the same
                .sf-search-dark switch the preview canvas does and every descendant rule follows. */}
            <div className={isDark ? 'sf-search-dark' : undefined} style={{ width: ROW_WIDTH, transform: `scale(${ROW_SCALE})`, transformOrigin: 'top left' }}>
                <div className='sf-search-result' style={effectiveStyles?.card}>
                    <div className='sf-search-result-image' style={imageStyle}>
                        <img src={SAMPLE_IMAGE} alt='' style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    </div>
                    <div className='sf-search-result-body'>
                        <div className='sf-search-result-name' style={effectiveStyles?.title}>{ROW_SAMPLE_NAME}</div>
                        <div className='sf-search-result-actions'>
                            <div className='sf-search-result-price' style={effectiveStyles?.price}>{formatCurrency(SAMPLE_PRODUCT.basePrice ?? 0)}</div>
                            {/* Inert decoration, same as the card example's cart button. The pill's
                                own style keys (addToCartButton / addToCartLabel) are edited from a
                                different panel, so it paints from the stylesheet alone here. */}
                            <div className='sf-search-result-cart'>
                                <span className='sf-search-result-cart-icon'><Icon iconName='ShoppingCart' /></span>
                                <span className='sf-search-result-cart-label'>Add to Cart</span>
                            </div>
                        </div>
                        {/* Static context, not a styled slot: the meta chips have their own panel
                            (Row Meta). maxWidth is released because the stylesheet reserves room for
                            a wide desktop actions column that the sample line does not need. */}
                        <div className='sf-search-result-meta' style={{ maxWidth: 'none', gap: 16, whiteSpace: 'nowrap' }}>
                            <span>Manufacturer: <span className='sf-search-result-link'>Lenovo</span></span>
                            <span>
                                Category: <span className='sf-search-result-link'>Computers</span>
                                <span style={{ margin: '0 4px' }}>&gt;</span>
                                <span className='sf-search-result-link'>Laptops</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </ExampleFrame>
    );
};

export default ProductCardExample;
